import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ExportStatus } from '@api-types/enums/export-status.enum';
import {
  CompletedConsensusExportSseEventPayload,
  ConsensusExportSseEventPayload,
} from '@api-types/sse/event-payloads/consensus-export.sse-event-payload';
import { SseEventType } from '@api-types/sse/sse.types';
import { SseEventHandler } from '@core/sse/sse-event-handler';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  ConsensusExportDialogComponent,
  ConsensusExportDialogData,
  ConsensusExportTab,
} from 'consensus/components/dialogs/consensus-export-dialog/consensus-export-dialog.component';
import {
  selectConfigurationId,
  selectSelectedConsensusTemplateId,
} from 'demand-forecasting/store/router-state/router.selectors';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs';
import { ConsensusExportStateService } from './consensus-export-state.service';

/**
 * Reloads the LIST tab and toasts the user on terminal states. Mirrors the
 * analysis SSE handler: COMPLETED shows a sticky toast that, when tapped, opens
 * the export dialog on the LIST tab with the finished export highlighted.
 * PENDING is never emitted (the row is already visible from dispatch), so the
 * handler only sees PROCESSING / COMPLETED / FAILED.
 */
@Injectable()
export class ConsensusExportSseHandler implements SseEventHandler<
  typeof SseEventType.ConsensusExport
> {
  readonly eventType = SseEventType.ConsensusExport;

  private readonly stateService = inject(ConsensusExportStateService);
  private readonly toast = inject(ToastrService);
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);

  private readonly configurationId = this.store.selectSignal(
    selectConfigurationId,
  );
  private readonly consensusTemplateId = this.store.selectSignal(
    selectSelectedConsensusTemplateId,
  );

  handle(payload: ConsensusExportSseEventPayload): void {
    this.stateService.exports.reload();
    switch (payload.status) {
      case ExportStatus.COMPLETED:
        // Trigger auto-select in an already-open dialog (LIST tab). The dialog
        // guards by checking the row belongs to its current template's list, so
        // a completion for a different template is ignored there.
        this.stateService.lastCompletedExportId.set(payload.exportId);
        this.showExportReadyToast(payload);
        break;
      case ExportStatus.FAILED:
        this.toast.error(
          payload.failureReason ||
            this.translate.instant('CONSENSUS.EXPORT.TOAST_FAILED_MESSAGE'),
          this.translate.instant('CONSENSUS.EXPORT.TOAST_FAILED_TITLE'),
        );
        break;
      default:
        // PROCESSING — list reload above is enough; no toast.
        break;
    }
  }

  /**
   * Sticky success toast that opens the export dialog (LIST tab, finished
   * export highlighted) when tapped. It stays until dismissed so the user can
   * act on it whenever they're ready — matching the analysis export UX and the
   * toast copy ("open the export dialog to download").
   */
  private showExportReadyToast(
    payload: CompletedConsensusExportSseEventPayload,
  ): void {
    const toast = this.toast.success(
      this.translate.instant('CONSENSUS.EXPORT.TOAST_READY_MESSAGE'),
      this.translate.instant('CONSENSUS.EXPORT.TOAST_READY_TITLE'),
      {
        timeOut: 0, // stays until manually dismissed
        extendedTimeOut: 0,
        tapToDismiss: false,
        closeButton: true,
      },
    );

    toast.onTap.pipe(take(1)).subscribe(() => {
      this.toast.clear(toast.toastId);
      this.openExportDialog(payload.exportId);
    });
  }

  /**
   * Opens the export dialog on the LIST tab for the consensus template the user
   * is currently viewing, highlighting the finished export. The SSE payload
   * carries no template context, so this relies on the active route — it works
   * while the user is still on the consensus page that started the export (the
   * common case). If the route context is gone, the tap just dismisses.
   */
  private openExportDialog(highlightExportId: string): void {
    const alreadyOpen = this.dialog.openDialogs.some(
      (ref) => ref.componentInstance instanceof ConsensusExportDialogComponent,
    );
    if (alreadyOpen) {
      // A dialog is already open — its auto-select effect picks up the finished
      // row; don't stack a second dialog on top.
      return;
    }
    const configurationId = this.configurationId();
    const consensusTemplateId = this.consensusTemplateId();
    if (!configurationId || !consensusTemplateId) {
      return;
    }
    const data: ConsensusExportDialogData = {
      configurationId,
      consensusTemplateId,
      initialTab: ConsensusExportTab.LIST,
      highlightExportId,
    };
    this.dialog.open(ConsensusExportDialogComponent, {
      data,
      autoFocus: false,
    });
  }
}
